/**
 * The one notification bell.
 *
 * Every module writes to the same feed — accounting approvals and compliance
 * reminders, HR leave and attendance requests, notices — so there is one
 * unread count and one list, scoped to the company being worked in.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck } from 'lucide-react';
import { get, post } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useSession } from '@/stores/session';

interface NotificationRow {
  id: number;
  module: string | null;
  kind: string | null;
  severity: string | null;
  title: string;
  body: string | null;
  link: string | null;
  is_read: number | string;
  created_at: string;
}

interface Feed {
  rows: NotificationRow[];
  unread: number;
}

const MODULE_LABEL: Record<string, string> = {
  hr: 'HR',
  accounting: 'Accounts',
  erp: 'Operations',
  field: 'Field',
};

const SEVERITY_DOT: Record<string, string> = {
  ERROR: 'bg-critical-500',
  WARNING: 'bg-caution-500',
  SUCCESS: 'bg-positive-500',
};

/** "5m", "3h", "2d" — or the date once it is more than a week old. */
function ago(stamp: string): string {
  const then = new Date(stamp.includes('T') ? stamp : `${stamp.replace(' ', 'T')}Z`).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  if (days <= 7) return `${days}d`;
  return new Date(then).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

export function NotificationBell() {
  const companyId = useSession((s) => s.company?.id ?? null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const key = ['platform-notifications', companyId];
  const { data } = useQuery({
    queryKey: key,
    enabled: !!companyId,
    queryFn: () => get<Feed>('/utilities/notifications', { limit: 30 }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const markRead = useMutation({
    mutationFn: (ids: number[] | null) => post('/utilities/notifications/read', { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform-notifications'] }),
  });

  if (!companyId) return null;

  const rows = data?.rows ?? [];
  const unread = Number(data?.unread ?? 0);

  const openRow = (row: NotificationRow) => {
    if (!Number(row.is_read)) markRead.mutate([Number(row.id)]);
    setOpen(false);
    if (row.link) navigate(row.link);
  };

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        className="relative grid h-9 w-9 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Bell className="h-[18px] w-[18px]" />
        {unread > 0 && (
          <span className="absolute right-1 top-1 grid h-4 min-w-4 place-items-center rounded-full bg-critical-600 px-1 text-[13px] font-semibold leading-none text-white tabular">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          <div
            role="dialog"
            aria-label="Notifications"
            className="absolute right-0 z-50 mt-1.5 w-[min(360px,calc(100vw-1.5rem))] overflow-hidden rounded-lg border border-border bg-popover shadow-raised"
          >
            <div className="flex items-center justify-between border-b border-border px-3 py-2">
              <span className="text-[15px] font-semibold">Notifications</span>
              {unread > 0 && (
                <button
                  type="button"
                  onClick={() => markRead.mutate(null)}
                  disabled={markRead.isPending}
                  className="flex items-center gap-1 text-[14px] font-medium text-primary hover:underline disabled:opacity-50"
                >
                  <CheckCheck className="h-3.5 w-3.5" /> Mark all read
                </button>
              )}
            </div>

            {rows.length === 0 ? (
              <p className="px-3 py-8 text-center text-[15px] text-muted-foreground">You are all caught up.</p>
            ) : (
              <ul className="max-h-[420px] divide-y divide-border overflow-y-auto">
                {rows.map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      onClick={() => openRow(row)}
                      className={cn(
                        'flex w-full gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-accent/50',
                        !Number(row.is_read) && 'bg-primary/5',
                      )}
                    >
                      <span
                        aria-hidden
                        className={cn(
                          'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                          Number(row.is_read) ? 'bg-transparent' : SEVERITY_DOT[row.severity ?? ''] ?? 'bg-primary',
                        )}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline justify-between gap-2">
                          <span className={cn('truncate text-[15px]', !Number(row.is_read) && 'font-semibold')}>{row.title}</span>
                          <span className="shrink-0 text-[13px] text-muted-foreground">{ago(row.created_at)}</span>
                        </span>
                        {row.body && <span className="mt-0.5 line-clamp-2 block text-[14px] text-muted-foreground">{row.body}</span>}
                        {row.module && (
                          <span className="mt-1 inline-block rounded bg-muted px-1.5 py-px text-[13px] font-medium uppercase tracking-wide text-muted-foreground">
                            {MODULE_LABEL[row.module] ?? row.module}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
